import { preloadImage } from '../../../common/js/network-utils';
import {
  getVideoContentLength,
  isVideoTooHeavy,
  shouldCheckVideoContentLength,
  getVideoContentLengthFromBase64URL,
  isBase64VideoUrl,
  bytesToKiloBytes
} from './utils';
import { sendAnalyticsTrack } from '../../../common/js/TrackAnalyticsHelper';
import { logMessage } from '../../../common/js/sentry';
import { MediaFile } from './vast/media-file';
import { getVideoMbSize } from '../../../common/js/media-file-utils';

function createAbortableTimeout(
  timeout?: number
): {
  signal: AbortSignal;
  cleanup: () => void;
} {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  if (timeout) {
    timeoutId = setTimeout(() => controller.abort(), timeout);
  }

  const cleanup = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  };

  return { signal: controller.signal, cleanup };
}

function withTimeoutHandling<T>(
  promise: Promise<T>,
  cleanup: () => void
): Promise<T> {
  return promise.then(
    (result: T) => {
      cleanup();
      return result;
    },
    (error: Error) => {
      cleanup();
      if (error.name === 'AbortError') {
        throw new Error('Prefetch video timeout');
      }
      throw error;
    }
  );
}

function onPrefetchResponse(
  videoPrefetchReq: XMLHttpRequest,
  sendResponse: boolean = false
): string | ArrayBuffer | null {
  if (videoPrefetchReq.status !== 200) {
    throw new Error(
      `Prefetch video HTTP error: ${videoPrefetchReq.status} ${videoPrefetchReq.statusText}`
    );
  }

  return sendResponse
    ? videoPrefetchReq.response
    : videoPrefetchReq.responseText;
}

function initVideoPrefetchRequest(
  videoURL: string,
  mimeType?: string | null,
  async: boolean = true
): XMLHttpRequest {
  const videoPrefetchReq = new XMLHttpRequest();
  videoPrefetchReq.open('GET', videoURL, async);

  if (mimeType) {
    videoPrefetchReq.overrideMimeType(mimeType);
  }

  return videoPrefetchReq;
}

export function prefetchVideoURLAsync(
  videoURL: string,
  options: { timeout?: number } = {}
): Promise<string> {
  const { signal, cleanup } = createAbortableTimeout(options.timeout);

  return withTimeoutHandling(
    fetch(videoURL, { signal }).then((response: Response) => {
      if (!response.ok) {
        throw new Error(
          `Prefetch video HTTP error: ${response.status} ${response.statusText}`
        );
      }
      return response.text();
    }),
    cleanup
  );
}

export function prefetchVideoURL(
  videoURL: string,
  mimeType?: string | null,
  sendResponse: boolean = false
): string | ArrayBuffer | null {
  const videoPrefetchReq = initVideoPrefetchRequest(videoURL, mimeType, false);
  videoPrefetchReq.send(null);
  return onPrefetchResponse(videoPrefetchReq, sendResponse);
}

interface BlobUrlResult {
  url: string;
  blob: Blob | null;
}

function onPrefetchVideoBlobResponse(
  url: string,
  videoResponse: string
): BlobUrlResult {
  let blob: Blob | null = null;
  try {
    blob = new Blob([Uint8Array.from(videoResponse, c => c.charCodeAt(0))]);
    url = URL.createObjectURL(blob);
  } catch (e) {
    logMessage('Error with precaching on ios ' + e);
  }

  return { url, blob };
}

export function prefetchVideoBlobUrlAsync(
  videoURL: string = '',
  timeout?: number
): Promise<BlobUrlResult> {
  if (!videoURL.includes('.mp4')) {
    return Promise.reject(
      new Error('use mp4 file only to generate base64 file.')
    );
  }

  const { signal, cleanup } = createAbortableTimeout(timeout);

  return withTimeoutHandling(
    fetch(videoURL, { signal })
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error(
            `Prefetch blob video HTTP error: ${response.status} ${response.statusText}`
          );
        }
        return response.blob();
      })
      .then((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        return { url, blob };
      }),
    cleanup
  );
}

export function prefetchVideoBlobUrl(videoURL: string = ''): BlobUrlResult {
  if (!videoURL.includes('.mp4')) {
    throw new Error('use mp4 file only to generate base64 file.');
  }

  const videoResponse = prefetchVideoURL(
    videoURL,
    'text/plain; charset=x-user-defined',
    true
  ) as string;
  return onPrefetchVideoBlobResponse(videoURL, videoResponse);
}

export function prefetchImages(images: (string | undefined)[] = []): void {
  images.filter(x => x).forEach(img => preloadImage(img as string));
}

export function createLinkPreloadTag(url: string): void {
  const preloadLink = document.createElement('link');
  preloadLink.href = url;
  preloadLink.rel = 'preload';
  (preloadLink as any).as = 'video';
  document.getElementsByTagName('head')[0].appendChild(preloadLink);
}

export function checkVideoContentLength(
  mediaFile: MediaFile,
  maxFileSize: number,
  blob: Blob | null
): void {
  if (!shouldCheckVideoContentLength()) {
    return;
  }

  const {
    additionalBitrate,
    bitrate,
    duration,
    height,
    url,
    width
  } = mediaFile;

  const isBlob = blob instanceof Blob;
  const isBase64 = isBase64VideoUrl(url);

  let contentLengthPromise: Promise<number>;
  if (isBlob && blob) {
    contentLengthPromise = Promise.resolve(bytesToKiloBytes(blob.size));
  } else if (isBase64) {
    contentLengthPromise = Promise.resolve(
      getVideoContentLengthFromBase64URL(url)
    );
  } else {
    contentLengthPromise = getVideoContentLength(url);
  }

  contentLengthPromise.then(contentLength => {
    if (isVideoTooHeavy(contentLength, maxFileSize)) {
      sendAnalyticsTrack(
        {},
        undefined,
        false,
        undefined,
        'video_file_too_large',
        undefined,
        undefined,
        {
          video_file_size: contentLength
        }
      );
    }

    const expectedVideoFileSize = getVideoMbSize(mediaFile as any);
    const realBitrate = contentLength / Number(duration || 1);
    sendAnalyticsTrack(
      {},
      undefined,
      false,
      undefined,
      'video_meta_information',
      undefined,
      undefined,
      {
        media_file_url: url,
        media_file_width: width,
        media_file_height: height,
        media_file_bitrate: bitrate,
        media_file_additional_bitrate: additionalBitrate,
        media_file_duration: duration,
        expected_video_file_size: Math.round(expectedVideoFileSize),
        real_video_file_size_raw: contentLength,
        real_bitrate: Math.round(realBitrate),
        delta_bitrate: Math.round(
          realBitrate -
            ((mediaFile.bitrate || 0) + (mediaFile.additionalBitrate || 0))
        )
      }
    );
  });
}
